import { useState } from "react";
import { Link } from "react-router";

import { useLoginWithCode } from "@/lib/api";
import { logger } from "@/lib/logger";

import { PillButton } from "./components";

/**
 * Figma 525:129 (adapted) — access-code entry. Replaces magic-link email for
 * the pilot: the household types the 6-digit code from their kit / the code
 * their VELUX contact shared, and the backend issues the session cookie.
 */
interface CodeEntryProps {
  /**
   * Message to show on arrival — set when the flyer's QR link carried a code
   * the backend rejected, so the household sees why they're being asked to
   * type it rather than a blank form that looks like the link did nothing.
   */
  initialError?: string;
}

export function CodeEntry({ initialError = "" }: CodeEntryProps) {
  const login = useLoginWithCode();
  const [code, setCode] = useState("");
  const [error, setError] = useState(initialError);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (login.isPending) {
      return;
    }
    setError("");
    logger.debug("[onboarding] submitting access code");
    try {
      await login.mutateAsync({ code });
      // On success the session cookie is set and the state query is
      // invalidated — OnboardingPage re-renders into the flow.
      logger.debug("[onboarding] code accepted");
    } catch (err) {
      const status = (err as { status?: number } | undefined)?.status;
      logger.warn("[onboarding] code login failed:", err);
      setError(
        status === 400
          ? "Please enter the 6-digit code."
          : "That code isn't valid. Please check and try again.",
      );
    }
  };

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-[390px] flex-col bg-white">
      <div className="flex h-[280px] items-center justify-center overflow-hidden">
        <img
          src="/brand/onboarding/kit-box.png"
          alt="Your VELUX Smart Climate starter kit"
          className="h-[260px] w-[340px] object-cover"
        />
      </div>
      <div className="flex flex-1 flex-col justify-between px-6 pt-3 pb-8">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-3">
            <h1 className="text-primary type-title">Enter your code</h1>
            <p className="text-muted text-[17px] leading-6">
              Type the 6-digit code from your VELUX kit to load your setup.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="flex flex-col gap-8">
            <div className="flex flex-col gap-2">
              <label htmlFor="code" className="text-primary text-[15px] leading-[22px]">
                Access code
              </label>
              <input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
                className="border-border placeholder:text-muted text-primary focus:border-ring h-16 w-full rounded-xl border bg-white text-center text-[28px] font-semibold tracking-[0.5em] outline-none"
              />
            </div>
            {error && (
              <p role="alert" className="text-destructive -mt-4 text-[15px]">
                {error}
              </p>
            )}
            <PillButton
              type="submit"
              label="Continue"
              busy={login.isPending}
              busyLabel="Checking…"
            />
          </form>
        </div>
        <div className="flex flex-col gap-2 pt-10 text-center">
          {/* Plain text, no link: the code is already in an email the
              household has, so pointing them somewhere to ask for it sends
              them away from the one place it is written down. */}
          <p className="text-muted text-[15px] leading-[22px]">
            Your code is printed on the sheet in your VELUX kit.
          </p>
          <p className="text-muted text-[13px] leading-4">
            VELUX team?{" "}
            <Link to="/login" className="underline [text-underline-position:from-font]">
              Log in with password
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
